import { ErrorHandler, inject, Injectable, Injector } from '@angular/core';
import { BD_RELOAD_PAGE, reloadIfAllowed } from './reload-helper';
import { BdUserTrackingService } from './tracking.service';

// Chunk errors surface with different shapes depending on bundler + browser:
// Webpack throws an Error with `name === 'ChunkLoadError'`, esbuild/Vite lazy
// loads produce browser-native messages. We match all known forms; each string
// branch is a real production signal we've seen, not speculation.
function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  const message = error.message ?? '';
  return (
    error.name === 'ChunkLoadError' ||
    message.includes('Loading chunk') ||
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed')
  );
}

@Injectable({ providedIn: 'root' })
export class BdUserErrorHandler extends ErrorHandler {
  private readonly _injector = inject(Injector);
  private readonly _reload = inject(BD_RELOAD_PAGE);

  override handleError(error: unknown): void {
    const originalError =
      (error as { rejection?: unknown })?.rejection ?? error;

    super.handleError(error);

    const trackingService = this._injector.get(BdUserTrackingService);
    trackingService.trackException(error);

    if (isChunkLoadError(originalError)) {
      const outcome = reloadIfAllowed(this._reload);
      // Surface chunk-error recovery in telemetry: `reloaded` is the normal
      // post-deploy self-heal; `suppressed` means the reload cap was hit and
      // the app is likely stuck on a broken deploy — the case to alert on.
      if (outcome === 'reloaded') {
        trackingService.trackEvent('app_update.reload', {
          reason: 'chunk-error',
        });
      } else if (outcome === 'suppressed') {
        trackingService.trackEvent('app_update.reload_suppressed', {
          reason: 'chunk-error',
        });
      }
    }
  }
}
